/**
 * Enhanced Pricing Breakdown Component
 *
 * Displays detailed pricing with:
 * - Line items with included tonnage
 * - Conditional warnings for contamination and overweight fees
 * - IWMA fee disclosure
 * - Mobile-optimized collapsible sections
 * - Tooltips for complex fees
 *
 * Implements UX recommendations:
 * - Progressive disclosure for complex information
 * - Clear warnings in context
 * - Mobile-first collapsible design
 */

import { useState } from 'react';
import { ChevronDown, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tooltip } from '../widget/Tooltip';
import { formatCentsAsCurrency } from '../../utils/formatCurrency';
import { cn } from '../../lib/utils';

export interface PricingLineItem {
  id: string;
  label: string;
  amount: number;
  included?: boolean;
  tooltip?: string;
  type?: 'base' | 'fee' | 'conditional' | 'warning';
}

export interface ConditionalWarning {
  id: string;
  condition: string;
  description: string;
  estimatedCost: number | { min: number; max: number };
  severity: 'info' | 'warning' | 'error';
}

export interface EnhancedPricingBreakdownProps {
  lineItems: PricingLineItem[];
  conditionalWarnings?: ConditionalWarning[];
  subtotal: number;
  iwmaFee?: {
    amount?: number;
    description: string;
    appliedAtService?: boolean;
  };
  total: number;
  estimatedTotal?: boolean;
  disclaimer?: string;
  className?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

/**
 * Format conditional cost range
 */
function formatConditionalCost(cost: number | { min: number; max: number }): string {
  if (typeof cost === 'number') {
    return formatCentsAsCurrency(cost);
  }
  return `${formatCentsAsCurrency(cost.min)}-${formatCentsAsCurrency(cost.max)}`;
}

/**
 * Get warning colors based on severity
 */
function getWarningColors(severity: 'info' | 'warning' | 'error') {
  switch (severity) {
    case 'error':
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-900',
        icon: 'text-red-600',
      };
    case 'warning':
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-900',
        icon: 'text-amber-600',
      };
    default:
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-900',
        icon: 'text-blue-600',
      };
  }
}

/**
 * Enhanced Pricing Breakdown Component
 */
export function EnhancedPricingBreakdown({
  lineItems,
  conditionalWarnings = [],
  subtotal,
  iwmaFee,
  total,
  estimatedTotal = true,
  disclaimer,
  className,
  collapsible = false,
  defaultExpanded = true,
}: EnhancedPricingBreakdownProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showWarnings, setShowWarnings] = useState(true);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <Card className={cn('shadow-sm', className)}>
      <CardHeader className={cn(
        'pb-4',
        collapsible && 'cursor-pointer hover:bg-gray-50 transition-colors'
      )}
      onClick={collapsible ? toggleExpanded : undefined}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Pricing Breakdown</CardTitle>
          {collapsible && (
            <ChevronDown
              className={cn(
                'h-5 w-5 text-gray-400 transition-transform',
                isExpanded && 'rotate-180'
              )}
            />
          )}
        </div>
      </CardHeader>

      {(!collapsible || isExpanded) && (
        <CardContent className="space-y-4">
          {/* Line Items */}
          <div className="space-y-3">
            {lineItems.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      'text-sm',
                      item.type === 'warning' ? 'font-semibold text-amber-900' : 'text-gray-700'
                    )}>
                      {item.label}
                    </span>
                    {item.tooltip && (
                      <Tooltip content={item.tooltip}>
                        <Info className="h-4 w-4 text-gray-400 cursor-help" />
                      </Tooltip>
                    )}
                  </div>
                  {item.included && (
                    <div className="flex items-center gap-1 mt-1">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-700">Included in base price</span>
                    </div>
                  )}
                </div>
                <span className={cn(
                  'text-sm font-semibold whitespace-nowrap',
                  item.type === 'warning' ? 'text-amber-900' : 'text-gray-900'
                )}>
                  {formatCentsAsCurrency(item.amount)}
                </span>
              </div>
            ))}
          </div>

          {/* Conditional Warnings */}
          {conditionalWarnings.length > 0 && showWarnings && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">Conditional Charges:</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWarnings(false)}
                  className="text-xs h-auto py-1 px-2"
                >
                  Hide
                </Button>
              </div>
              {conditionalWarnings.map((warning) => {
                const colors = getWarningColors(warning.severity);
                return (
                  <div
                    key={warning.id}
                    className={cn(
                      'p-3 rounded-lg border',
                      colors.bg,
                      colors.border
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className={cn('h-4 w-4 flex-shrink-0 mt-0.5', colors.icon)} />
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-xs font-semibold mb-1', colors.text)}>
                          {warning.condition}
                        </p>
                        <p className={cn('text-xs mb-2', colors.text)}>
                          {warning.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={cn('text-xs font-medium', colors.text)}>
                            Potential charge:
                          </span>
                          <span className={cn('text-sm font-bold', colors.text)}>
                            +{formatConditionalCost(warning.estimatedCost)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!showWarnings && conditionalWarnings.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowWarnings(true)}
              className="w-full text-xs"
            >
              Show Conditional Charges ({conditionalWarnings.length})
            </Button>
          )}

          <Separator />

          {/* Subtotal */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Subtotal</span>
            <span className="text-sm font-bold text-gray-900">
              {formatCentsAsCurrency(subtotal)}
            </span>
          </div>

          {/* IWMA Fee */}
          {iwmaFee && (
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">IWMA State Fee</span>
                    <Tooltip content={iwmaFee.description}>
                      <Info className="h-4 w-4 text-gray-400 cursor-help" />
                    </Tooltip>
                  </div>
                  {iwmaFee.appliedAtService && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Applied at time of service
                    </p>
                  )}
                </div>
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {iwmaFee.amount ? formatCentsAsCurrency(iwmaFee.amount) : 'TBD'}
                </span>
              </div>
            </div>
          )}

          <Separator className="border-t-2" />

          {/* Total */}
          <div className="flex items-center justify-between py-2">
            <div>
              <span className="text-lg font-bold text-gray-900">
                {estimatedTotal ? 'Estimated Total' : 'Total'}
              </span>
              {estimatedTotal && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Estimate
                </Badge>
              )}
            </div>
            <span className="text-2xl font-bold text-primary-600">
              {formatCentsAsCurrency(total)}
              {iwmaFee?.appliedAtService && '+'}
            </span>
          </div>

          {/* Disclaimer */}
          {disclaimer && (
            <>
              <Separator />
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600 leading-relaxed">
                  {disclaimer}
                </p>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Sticky Pricing Summary (Mobile)
 * Fixed to bottom of screen on mobile for easy access
 */
interface StickyPricingSummaryProps {
  total: number;
  estimatedTotal?: boolean;
  onViewDetails: () => void;
  className?: string;
}

export function StickyPricingSummary({
  total,
  estimatedTotal = true,
  onViewDetails,
  className,
}: StickyPricingSummaryProps) {
  return (
    <div className={cn(
      'fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-lg p-4 z-40 md:hidden',
      className
    )}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-gray-600">
            {estimatedTotal ? 'Estimated Total' : 'Total'}
          </p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCentsAsCurrency(total)}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onViewDetails}
          className="whitespace-nowrap"
        >
          View Details
        </Button>
      </div>
    </div>
  );
}
